import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  FormInstance,
  FormListFieldData,
  Image,
  Row,
  Space,
  Switch,
} from "antd";
import { useEffect } from "react";
import DeleteIcon from "../../../../../../assets/icons/DeleteIcon";
import EditIcon from "../../../../../../assets/icons/EditIcon";
import Bank from "../../../../../../assets/images/bank.svg";
import ConfirmModal from "../../../../../../components/ui/ConfirmModal";
import { useDeletePaymentInfoMutation } from "../../../../../../features/Vendor/vendor.api";
import useError from "../../../../../../hooks/useError";
import useModal from "../../../../../../hooks/useModal";
import { toast } from "../../../../../../lib/toast";
import { ModalData, ModalType } from "../Payments";
type Props = {
  onRemove: () => void;
  field: { key: number; name: number; fieldKey?: number | undefined };
  handleOpen: ({ type, data }: { data: ModalData; type: ModalType }) => void;
  data: any;
  fields: FormListFieldData[];
  form: FormInstance<any>;
};

export default function BankCard({
  onRemove,
  field,
  handleOpen,
  data,
  fields,
  form,
}: Props) {
  const {
    open,
    handleOpen: hOpen,
    handleClose,
  } = useModal<unknown, "REMOVE-BANK">();
  const [deleteBank, { isLoading, isSuccess, isError, error }] =
    useDeletePaymentInfoMutation();

  useEffect(() => {
    if (isSuccess) {
      onRemove && onRemove();
      toast("success", "Bank account deleted successfully.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  useError(isError, error, "Bank delete failed.");

  if (!data?.bankAccount) return null;
  const { bankAccount } = data || {};

  const {
    account_name,
    account_number,
    bank_name,
    branch_name,
    routing_number,
    swift_code,
    bank_logo,
  } = bankAccount || {};
  return (
    <Card className="shadow-sm rounded-xl !w-full border-none bg-theme-slate-25">
      <Row gutter={[16, 16]}>
        {/* Logo */}
        <Col span={24} style={{ textAlign: "left" }}>
          <Flex justify="space-between" align="start">
            <Image
              src={bank_logo || Bank}
              alt="City Bank"
              height={80}
              preview={false}
            />
            <Space>
              <Button
                onClick={() =>
                  handleOpen({
                    type: "UPDATE-BANK",
                    data: { index: field.name },
                  })
                }
                className="border-none bg-transparent shadow-none size-8 p-0 hover:!bg-transparent"
              >
                <EditIcon height={24} width={24} />
              </Button>
              <Button
                onClick={() => hOpen({ type: "REMOVE-BANK" })}
                className="border-none bg-transparent shadow-none size-8 p-0 hover:!bg-transparent"
              >
                <DeleteIcon height={24} width={24} />
              </Button>
            </Space>
          </Flex>
        </Col>

        {/* Left column */}
        <Col span={12}>
          <p className="text-theme-gray-500 font-medium text-sm">Bank Name</p>
          <p className="text-theme-gray-900 font-medium text-sm mb-1.5">
            {bank_name}
          </p>

          <p className="text-theme-gray-500 font-medium text-sm">
            Account Name
          </p>
          <p className="text-theme-gray-900 font-medium text-sm mb-1.5">
            {account_name}
          </p>

          <p className="text-theme-gray-500 font-medium text-sm">
            Routing Number
          </p>
          <p className="text-theme-gray-900 font-medium text-sm">
            {routing_number}
          </p>
        </Col>

        {/* Right column */}
        <Col span={12}>
          <p className="text-theme-gray-500 font-medium text-sm">Branch Name</p>
          <p className="text-theme-gray-900 font-medium text-sm mb-1.5">
            {branch_name}
          </p>

          <p className="text-theme-gray-500 font-medium text-sm">
            Account Number
          </p>
          <p className="text-theme-gray-900 font-medium text-sm mb-1.5">
            {account_number}
          </p>

          <p className="text-theme-gray-500 font-medium text-sm">SWIFT Code</p>
          <p className="text-theme-gray-900 font-medium text-sm">
            {swift_code || "N/A"}
          </p>
        </Col>
      </Row>
      {/* Switch at bottom */}
      <Flex align="center" gap={8}>
        <Form.Item
          className="mb-0"
          name={[field.name, "is_default"]}
          valuePropName="checked"
          initialValue={false}
        >
          <Switch
            onChange={(checked) => {
              if (checked) {
                fields.forEach((f, i) => {
                  if (i !== field.name) {
                    form.setFieldValue(["paymentInfo", i, "is_default"], false);
                  }
                });
              }
            }}
          />
        </Form.Item>
        <span className="text-theme-gray-24 font-medium">Set as Default</span>
      </Flex>

      <ConfirmModal
        closable
        icon="DELETE"
        okText={"Yes, Delete"}
        confirmTitle="Remove Bank Account"
        questionText={`Are you sure you want to delete bank account of “${bank_name}”`}
        open={open.open}
        handleClose={handleClose}
        okLoading={isLoading}
        handleOk={() => {
          data?.vendor_id
            ? deleteBank({ vendorId: data?.vendor_id, paymentInfoId: data?.id })
            : onRemove();
        }}
      />
    </Card>
  );
}
